import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessIntegrationsAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';

/**
 * Business → Integrations → Google Sheets.
 *
 * Org admins configure per-tenant Google Sheets credentials used by
 * Uni Forms live response sync and report sheet export.  Secrets are
 * stored in ``uni_forms.UniFormsConfig`` (tenant schema), not in global
 * ``settings.py``.  Environment variables remain as a deployment fallback.
 */
export default function BusinessGoogleSheets() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [serviceAccountFile, setServiceAccountFile] = useState('');
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [clearJson, setClearJson] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await BusinessIntegrationsAPI.googleSheets.get();
      setStatus(payload);
      setEnabled(Boolean(payload.enabled));
      setServiceAccountFile(payload.google_service_account_file || '');
      setServiceAccountJson('');
      setClearJson(false);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        enabled,
        google_service_account_file: serviceAccountFile,
      };
      if (clearJson) {
        body.google_service_account_json = '';
      } else if (serviceAccountJson.trim()) {
        body.google_service_account_json = serviceAccountJson.trim();
      }
      const payload = await BusinessIntegrationsAPI.googleSheets.update(body);
      setStatus(payload);
      setServiceAccountJson('');
      setClearJson(false);
      notify.success('Google Sheets settings saved.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [enabled, serviceAccountFile, serviceAccountJson, clearJson]);

  if (loading && !status) return <Loader label="Loading Google Sheets settings…" />;

  const configured = Boolean(status?.configured);
  const source = status?.credentials_source || 'none';

  return (
    <>
      <PageHeader
        title="📊 Google Sheets"
        subtitle={
          'Configure service-account credentials for Uni Forms response sync ' +
          'and report exports. Each organisation uses its own settings.'
        }
        actions={
          <>
            <button
              type="button"
              className="ty-btn ty-btn--ghost"
              onClick={reload}
              disabled={loading || saving}
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              className="ty-btn"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </>
        }
      />

      <ErrorBox error={error} />

      <StatusBanner configured={configured} source={source} status={status} />

      <form className="ty-card" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <label className="ty-field" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={saving}
            style={{ marginTop: 4 }}
          />
          <span>
            <strong>Enable Google Sheets sync</strong>
            <span style={{ display: 'block', fontSize: 13, color: 'var(--ty-text-muted)', marginTop: 4 }}>
              When enabled, complete form responses append to linked spreadsheets
              and report exports can refresh Google Sheets.
            </span>
          </span>
        </label>

        <label className="ty-field">
          <span>Service account JSON key</span>
          <textarea
            className="ty-textarea"
            rows={6}
            placeholder={
              status?.has_service_account_json
                ? 'JSON key is stored — paste a new value to replace, or tick Clear below.'
                : 'Paste the entire service account JSON key file contents'
            }
            value={serviceAccountJson}
            onChange={(e) => setServiceAccountJson(e.target.value)}
            disabled={saving || clearJson}
          />
          {status?.has_service_account_json && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
              <input
                type="checkbox"
                checked={clearJson}
                onChange={(e) => setClearJson(e.target.checked)}
                disabled={saving}
              />
              <span>Clear stored JSON key</span>
            </label>
          )}
        </label>

        <label className="ty-field">
          <span>Service account file path (optional)</span>
          <input
            type="text"
            className="ty-input"
            placeholder="/path/to/service-account.json on the app server"
            value={serviceAccountFile}
            onChange={(e) => setServiceAccountFile(e.target.value)}
            disabled={saving}
          />
          <span style={{ fontSize: 12, color: 'var(--ty-text-muted)' }}>
            Use either pasted JSON or a server file path — not both unless JSON is empty.
          </span>
        </label>

        <SetupHelp />
      </form>
    </>
  );
}

function StatusBanner({ configured, source, status }) {
  const ok = configured && status?.enabled;
  const colour = ok ? '#5fd788' : '#f9771d';
  const bg = ok ? 'rgba(95, 215, 136, 0.10)' : 'rgba(249, 119, 29, 0.10)';
  const title = ok
    ? 'Google Sheets sync is enabled and credentials are configured.'
    : !status?.enabled
      ? 'Google Sheets sync is disabled for this organisation.'
      : 'Credentials are missing — sync will be skipped until you add them.';

  return (
    <div
      className="ty-card"
      style={{ borderLeft: `4px solid ${colour}`, background: bg, marginBottom: 16 }}
    >
      <strong>{title}</strong>
      <div style={{ fontSize: 13, color: 'var(--ty-text-muted)', marginTop: 6 }}>
        Credentials source: <strong>{source}</strong>
        {status?.environment_fallback_available && source !== 'environment' && (
          <> · Server environment variables are also available as fallback</>
        )}
        {status?.has_service_account_json && <> · JSON key stored</>}
      </div>
    </div>
  );
}

function SetupHelp() {
  return (
    <div style={{ fontSize: 13, color: 'var(--ty-text-muted)', marginTop: 16, lineHeight: 1.5 }}>
      <strong>Setup checklist</strong>
      <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
        <li>Create a Google Cloud service account with the Google Sheets API enabled.</li>
        <li>Paste the JSON key above or set a file path on the app server.</li>
        <li>Share each target spreadsheet with the service account email (Editor).</li>
        <li>Link forms via a <code>gsheets</code> integration with <code>spreadsheet_id</code>.</li>
      </ol>
    </div>
  );
}
